import "../TransactionPanelShell.css";

import { ReactElement } from "react";
import { createPortal } from "react-dom";

import {
  CollaboratorTypeConstants,
  InteractionObjectType,
  ProjectIssuanceConstants,
  ProjectIssuanceTransactionConstants,
} from "../../../../constants";
import { UploadDocumentModal } from "../../../../route/shared/projects/project/documents/components/UploadDocumentModal";
import { SearchIssuanceTransactionsResponse } from "../../../../service/query";
import { formatNumber, isPastDateByDay } from "../../../../utils";
import {
  ActionButtons,
  Column,
  DatePicker,
  ErrorBox,
  FlyoutPanel,
  FormLabel,
  PillsList,
  ReadonlyText,
  Row,
  TextInput,
} from "../../../../widget";
import { isUnitKmHabitatType } from "../../../IssuancePanel";
import { TagsPanel } from "../../../TagsPanel";
import { QuoteExpiryField } from "../QuoteExpiryField";
import { TransactionAttachments } from "../TransactionAttachments";
import { TransactionCompanyPicker } from "../TransactionCompanyPicker";
import { useEditIssuanceTransactionPanel } from "./useEditIssuanceTransactionPanel";

interface EditIssuanceTransactionPanelProps {
  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0];
  variant?: "hub" | "seed";
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

export const EditIssuanceTransactionPanel = ({
  issuanceTransaction,
  variant = "seed",
  show,
  onClose,
  handleRefresh,
}: EditIssuanceTransactionPanelProps): JSX.Element => {
  const {
    errors,
    handleClosePanel,
    handleSubmit,
    isHandleSubmitLoading,
    issuanceTransactionPanelData,
    setIssuanceTransactionPanelData,
    showTagsPanel,
    setShowTagsPanel,
    tagPills,
    setRefreshTags,
    documents,
    canManageAttachments,
    canEdit,
    projectUuid,
    showUploadDocumentModal,
    handleAddAttachment,
    handleCloseUploadDocumentModal,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
  } = useEditIssuanceTransactionPanel({
    issuanceTransaction,
    show,
    onClose,
    handleRefresh,
  });

  const corners = variant === "seed" ? "rounded" : "default";
  const font = variant === "seed" ? "figtree" : "default";

  const content = (): ReactElement => {
    return (
      <form className="TransactionPanel_form" onSubmit={(e) => handleSubmit(e)}>
        {errors && errors.length > 0 && (
          <Row spacingV="ll">
            <Column span={12}>
              <ErrorBox text={errors.map((error) => error.message)} />
            </Column>
          </Row>
        )}
        {issuanceTransaction.parentTransactionId && (
          <Row spacingV="ll">
            <Column span={12}>
              <ReadonlyText label="Previous transaction ID" value={issuanceTransaction.parentTransactionId} />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            <TransactionCompanyPicker
              label="Counterparty"
              show={show}
              selectedUuid={issuanceTransactionPanelData.counterPartyCollaboratorUuid}
              selectedName={issuanceTransactionPanelData.counterPartyName}
              collaboratorTypes={CollaboratorTypeConstants.COUNTERPARTY_TYPES}
              variant={variant}
              disabled={!canEdit}
              onChange={(company) =>
                setIssuanceTransactionPanelData((prev) => ({
                  ...prev,
                  counterPartyCollaboratorUuid: company?.uuid ?? null,
                  counterPartyName: company?.name ?? null,
                }))
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TransactionCompanyPicker
              label="Broker"
              show={show}
              selectedUuid={issuanceTransactionPanelData.brokerCollaboratorUuid}
              selectedName={issuanceTransactionPanelData.brokerName}
              collaboratorTypes={CollaboratorTypeConstants.BROKER_TYPES}
              variant={variant}
              disabled={!canEdit}
              onChange={(company) =>
                setIssuanceTransactionPanelData((prev) => ({
                  ...prev,
                  brokerCollaboratorUuid: company?.uuid ?? null,
                  brokerName: company?.name ?? null,
                }))
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            {/* DatePicker has no disabled state, so a caller without the grant reads the date instead. */}
            {canEdit ? (
              <DatePicker
                label="Date"
                value={issuanceTransactionPanelData.transactionDate ?? undefined}
                onChange={(value) =>
                  setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, transactionDate: value ?? null })
                }
                corners={corners}
                font={font}
              />
            ) : (
              <ReadonlyText
                label="Date"
                value={issuanceTransactionPanelData.transactionDate?.toLocaleDateString("en-GB")}
                noDataAvailable={!issuanceTransactionPanelData.transactionDate}
              />
            )}
          </Column>
        </Row>
        {/* Type is immutable on edit, so gate on the transaction's own type. */}
        {issuanceTransaction.type === ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_QUOTE && (
          <Row spacingV="ll">
            <Column span={12}>
              <QuoteExpiryField
                transactionType={issuanceTransaction.type}
                value={issuanceTransactionPanelData.expiryDate ?? undefined}
                onChange={(date) =>
                  setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, expiryDate: date ?? null })
                }
                variant={variant}
                disabled={!canEdit}
                isOverdue={
                  issuanceTransactionPanelData.expiryDate
                    ? isPastDateByDay(issuanceTransactionPanelData.expiryDate)
                    : false
                }
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Quantity"
              name="Quantity"
              type="number"
              value={issuanceTransactionPanelData.quantity?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  quantity: value === "" ? null : parseFloat(Number(value).toFixed(5)),
                  totalValue:
                    issuanceTransactionPanelData.price && value !== ""
                      ? parseFloat(Number(value).toFixed(5)) * issuanceTransactionPanelData.price -
                        (issuanceTransactionPanelData.fees ?? 0)
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={999999.99}
              disabled={!canEdit}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Price"
              name="Price"
              type="number"
              prefix="£"
              value={issuanceTransactionPanelData.price?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  price: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                  totalValue:
                    issuanceTransactionPanelData.quantity && value !== ""
                      ? parseFloat(Number(value).toFixed(4)) * issuanceTransactionPanelData.quantity -
                        (issuanceTransactionPanelData.fees ?? 0)
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
              disabled={!canEdit}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Fees"
              name="Fees"
              type="number"
              prefix="£"
              value={issuanceTransactionPanelData.fees?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  fees: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                  totalValue:
                    issuanceTransactionPanelData.price && issuanceTransactionPanelData.quantity
                      ? issuanceTransactionPanelData.price * issuanceTransactionPanelData.quantity -
                        (value === "" ? 0 : parseFloat(Number(value).toFixed(4)))
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
              disabled={!canEdit}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <ReadonlyText
              label="Total value"
              value={
                issuanceTransactionPanelData.totalValue !== null
                  ? `£${formatNumber(issuanceTransactionPanelData.totalValue)}`
                  : undefined
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Transaction reference"
              name="Transaction reference"
              value={issuanceTransactionPanelData.externalReference}
              onChange={(value) =>
                setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, externalReference: value })
              }
              disabled={!canEdit}
            />
          </Column>
        </Row>
        {issuanceTransaction.unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY && (
          <Row spacingV="ll">
            <Column span={12}>
              <TextInput
                label={`Unit size (${isUnitKmHabitatType(issuanceTransaction.habitatGroup) ? "Km" : "Ha"})`}
                name="Unit size Ha / Km"
                type="number"
                value={issuanceTransactionPanelData.areaHa?.toString()}
                onChange={(value) =>
                  setIssuanceTransactionPanelData({
                    ...issuanceTransactionPanelData,
                    areaHa: value === "" ? null : Number(value),
                  })
                }
                minNumberValue={0}
                disabled={!canEdit}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            <FormLabel label="Tags" />
            <PillsList
              pills={tagPills}
              linkText="+ Add a tag"
              readOnly={!canEdit}
              linkOnClick={() => setShowTagsPanel(true)}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TransactionAttachments
              documents={documents}
              variant={variant}
              readOnly={!canManageAttachments}
              onAddAttachment={handleAddAttachment}
              onRemove={handleRemoveAttachment}
              onDownload={handleDownloadAttachment}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <ActionButtons
              submitButtonLabel="Save changes"
              corners={corners}
              onCancel={handleClosePanel}
              isLoading={isHandleSubmitLoading}
              disabled={!canEdit}
            />
          </Column>
        </Row>
      </form>
    );
  };
  return (
    <FlyoutPanel
      show={show}
      onClose={handleClosePanel}
      size="ExtraSmall"
      height="Full"
      className="TransactionPanel_flyout"
      header="Edit transaction"
      stickyHeader={false}
      buttonsChildren={undefined}
      contentChildren={
        <>
          {content()}
          <TagsPanel
            objectUuid={issuanceTransaction.uuid}
            objectType={InteractionObjectType.ISSUANCE_TRANSACTION}
            show={showTagsPanel}
            onClose={() => setShowTagsPanel(false)}
            shouldRefreshTags={() => setRefreshTags((prev) => !prev)}
          />
          {/* Uploads a project document, which handleDocumentsUploaded then links to this transaction. */}
          {/*
            Portalled to the body because the flyout panel carries a transform, which makes it the containing
            block for position: fixed descendants. Left inside it, the modal centres on the narrow panel rather
            than the viewport and its right-hand side is cut off past the edge of the screen.
          */}
          {createPortal(
            <UploadDocumentModal
              show={showUploadDocumentModal}
              closeModal={handleCloseUploadDocumentModal}
              projectUuid={projectUuid}
              refreshDocuments={false}
              setRefreshDocuments={() => undefined}
              onUploaded={handleDocumentsUploaded}
              className="Modal_aboveFlyoutPanel"
            />,
            document.body
          )}
        </>
      }
    />
  );
};
